import type { ReactNode } from 'react';
import { useEffect, useRef, useId } from 'react';
import { Icon } from './Icon';
import { useScrollLock, useFocusTrap } from '../../lib/overlay';
import { useModalStore } from '../../stores/modal.store';

interface ModalProps {
  title: string;
  kicker?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
  /** Una classe in più sul pannello (es. modal--anteprima del Nuovo task con più righe). */
  className?: string;
}

export function Modal({ title, kicker, onClose, children, footer, size = 'md', className }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Quando la store è in fase di chiusura, applichiamo le classi --leaving
  // per far giocare l'animazione di uscita prima dello smontaggio.
  const closing = useModalStore((s) => s.closing);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Scroll-lock del body + focus trap dentro il pannello (+ return focus)
  useScrollLock(true);
  useFocusTrap(panelRef, true);

  return (
    <div className={`modal-backdrop${closing ? ' modal-backdrop--leaving' : ''}`} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={panelRef} className={`modal modal-${size}${className ? ` ${className}` : ''}${closing ? ' modal--leaving' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="modal-hd">
          <div>
            {kicker && <div className="kicker">{kicker}</div>}
            <div className="t-h2" id={titleId}>{title}</div>
          </div>
          <button className="btn btn-sm btn-ghost btn-icon" onClick={onClose} aria-label="Chiudi" data-noautofocus>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="modal-bd">{children}</div>
        {footer && <div className="modal-ft">{footer}</div>}
      </div>
    </div>
  );
}

interface FieldProps {
  label?: ReactNode;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  span?: number;
  className?: string;
}

export function Field({ label, hint, required, children, span = 12, className = '' }: FieldProps) {
  return (
    <div className={`col-span-${span} ${className}`} style={{ gridColumn: `span ${span}` }}>
      {label && (
        <label className="field-label">
          {label} {required && <span className="req">*</span>}
        </label>
      )}
      {children}
      {hint && <div className="field-hint">{hint}</div>}
    </div>
  );
}
